import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MediaDevices } from 'icons/untitledui';

import { isTouchDevice } from 'shared/helpers/device';
import { useShiftNoteCapture } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import {
  AttachmentRejection,
  validateImagePicks,
  validateMixedPicks,
  validateVideoPick,
} from 'shiftNotes/helpers/noteCaptureAttachmentLimits';
import * as Buttons from 'theme/components/Buttons.style';
import { HiddenFileInput } from 'shiftNotes/styles/shared.style';

export type AttachmentPickerOrigin = 'text' | 'recording' | 'paused';

interface UseNoteAttachmentPickerOptions {
  fromState: AttachmentPickerOrigin;
  onBeforePick?: () => void;
}

interface UseNoteAttachmentPickerResult {
  attachmentError: AttachmentRejection | null;
  handleRemoveAttachment: (file: File) => void;
  controls: JSX.Element;
}

const isVideo = (file: File) => file.type.startsWith('video/');

export default function useNoteAttachmentPicker({
  fromState,
  onBeforePick,
}: UseNoteAttachmentPickerOptions): UseNoteAttachmentPickerResult {
  const { t } = useTranslation('pageShiftNotes');
  const { attachments, setAttachments } = useShiftNoteCapture();
  const shiftNotesCapture = usePosthogCapture();
  const [attachmentError, setAttachmentError] =
    useState<AttachmentRejection | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);

  const canCaptureFromCamera = isTouchDevice();

  const hasVideo = attachments.some(isVideo);
  const hasImages = attachments.some((f) => !isVideo(f));

  const openPicker = (ref: React.RefObject<HTMLInputElement>) => {
    onBeforePick?.();
    ref.current?.click();
  };

  const readAndClear = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    return picked;
  };

  const captureImagesAdded = (count: number) => {
    Array.from({ length: count }).forEach(() =>
      shiftNotesCapture.captureAttachmentAdded({
        media_type: 'image',
        from_state: fromState,
      })
    );
  };

  const captureVideoAdded = () =>
    shiftNotesCapture.captureAttachmentAdded({
      media_type: 'video',
      from_state: fromState,
    });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = readAndClear(e);
    if (picked.length === 0) return;
    const { accepted, rejection } = validateImagePicks(picked, attachments);
    setAttachmentError(rejection);
    if (accepted.length === 0) return;
    setAttachments((prev) => [...prev, ...accepted]);
    captureImagesAdded(accepted.length);
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = readAndClear(e);
    if (picked.length === 0) return;
    const { accepted, rejection } = validateVideoPick(picked[0], attachments);
    setAttachmentError(rejection);
    if (accepted.length === 0) return;
    setAttachments(accepted);
    captureVideoAdded();
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = readAndClear(e);
    if (picked.length === 0) return;
    const { accepted, rejection } = validateMixedPicks(picked, attachments);
    setAttachmentError(rejection);
    if (accepted.length === 0) return;
    if (isVideo(accepted[0])) {
      setAttachments([accepted[0]]);
      captureVideoAdded();
      return;
    }
    setAttachments((prev) => [...prev.filter((f) => !isVideo(f)), ...accepted]);
    captureImagesAdded(accepted.length);
  };

  const handleRemoveAttachment = useCallback(
    (file: File) => {
      setAttachments((prev) => prev.filter((f) => f !== file));
      setAttachmentError(null);
      shiftNotesCapture.captureAttachmentRemoved({
        media_type: isVideo(file) ? 'video' : 'image',
        from_state: fromState,
      });
    },
    [fromState, setAttachments, shiftNotesCapture]
  );

  const controls = canCaptureFromCamera ? (
    <>
      <Buttons.SecondaryLightIcon
        $size="medium"
        onClick={() => openPicker(imageInputRef)}
        disabled={hasVideo}
        aria-label={t('noteCaptureAttachments.addPhoto')}
        data-testid="note-capture-photo-button"
      >
        <MediaDevices.Camera01 />
      </Buttons.SecondaryLightIcon>
      <Buttons.SecondaryLightIcon
        $size="medium"
        onClick={() => openPicker(videoInputRef)}
        disabled={hasImages}
        aria-label={t('noteCaptureAttachments.addVideo')}
        data-testid="note-capture-video-button"
      >
        <MediaDevices.VideoRecorder />
      </Buttons.SecondaryLightIcon>
      <HiddenFileInput
        ref={imageInputRef}
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleImageChange}
        data-testid="note-capture-photo-input"
      />
      <HiddenFileInput
        ref={videoInputRef}
        accept="video/*"
        capture="environment"
        onChange={handleVideoChange}
        data-testid="note-capture-video-input"
      />
    </>
  ) : (
    <>
      <Buttons.SecondaryLightIcon
        $size="medium"
        onClick={() => openPicker(mediaInputRef)}
        aria-label={t('noteCaptureAttachments.addMedia')}
        data-testid="note-capture-media-button"
      >
        <MediaDevices.ImagePlus />
      </Buttons.SecondaryLightIcon>
      <HiddenFileInput
        ref={mediaInputRef}
        accept="image/*,video/*"
        multiple
        onChange={handleMediaChange}
        data-testid="note-capture-media-input"
      />
    </>
  );

  return { attachmentError, handleRemoveAttachment, controls };
}
